<script lang="ts">
    import { writable } from "svelte/store";
    import { clickOutside } from "svelte-outside";
    import ActionBarButton from "../ActionBarButton.svelte";
    import LL from "../../../../i18n/i18n-svelte";
    import { userListStore } from "../../../Stores/UserListStore";
    import { gameManager } from "../../../Phaser/Game/GameManager";
    import { analyticsClient } from "../../../Administration/AnalyticsClient";
    import type { UserProviderMerger } from "../../../Chat/UserProviderMerger/UserProviderMerger";
    import RoomUserListWrapper from "../../../Chat/Components/UserList/RoomUserListWrapper.svelte";
    import { createFloatingUiActions } from "../../../Utils/svelte-floatingui";
    import { triggerClickOnEnterOrSpace } from "../../../Utils/svelte-a11y";
    import CloseIcon from "../../Icons/meta/CloseIcon.svelte";
    import { getColorByString } from "../../../Utils/ColorGenerator";
    import ParticipantWoka from "./ParticipantWoka.svelte";

    const userProviderMerger = writable<UserProviderMerger | undefined>(undefined);
    const gameScene = gameManager.getCurrentGameScene();
    gameScene.userProviderMerger.then((v) => userProviderMerger.set(v)).catch(console.error);

    // usersByRoomStore groups every connected room/map together — restrict the
    // avatar-stack preview to the current map, same as the popup it opens.
    let usersByRoom = $derived($userProviderMerger ? $userProviderMerger.usersByRoomStore : undefined);
    let users = $derived($usersByRoom?.get(gameScene.roomUrl)?.users);

    function toggleUserList() {
        if (!$userListStore) analyticsClient.openUserList();
        userListStore.toggle();
    }

    const [floatingUiRef, floatingUiContent] = createFloatingUiActions(
        {
            placement: "bottom",
        },
        8,
    );
</script>

<div
    use:floatingUiRef
    class="cursor-pointer pointer-events-auto"
    role="button"
    tabindex="0"
    onclick={(event) => {
        event.preventDefault();
        toggleUserList();
    }}
    onkeydown={triggerClickOnEnterOrSpace}
    aria-label="Пользователи"
    data-testid="user-list-button"
>
    {#if !$userListStore}
        <div class="flex items-center shrink-0 -space-x-4">
            {#if users && users.length > 0}
                {@const slicedUsers = users.slice(0, 3)}
                {#each slicedUsers as user, i ((user.spaceUserId || user.uuid || "") + i)}
                    {#if user.pictureStore}
                        <div
                            class="rounded-full overflow-hidden flex justify-center items-center w-10 @xl/actions:w-12 h-10 @xl/actions:h-12"
                            style={`z-index: ${slicedUsers.length - i}; background-color: ${getColorByString(
                                user.username,
                            )}`}
                            title={user.username}
                        >
                            <ParticipantWoka
                                pictureStore={user.pictureStore}
                                fallbackName={user.username || ""}
                                customWidth="50px"
                            />
                        </div>
                    {/if}
                {/each}
                {#if users.length > 3}
                    <div
                        class="participant-plus rounded-full bg-mlight/80 shadow-light w-10 @xl/actions:w-12 h-10 @xl/actions:h-12
                            backdrop-blur-sm flex items-center pr-1.5
                            justify-end shrink-0 text-mdark text-xs font-bold z-0"
                        style="animation-delay: {(users.length + 1) * 120}ms"
                        title={$LL.actionbar.participantListPlaceholder()}
                    >
                        +{users.length - 3}
                    </div>
                {/if}
            {/if}
        </div>
    {:else}
        <ActionBarButton disabledHelp dataTestId="user-list-close-button" ariaLabel="Закрыть список пользователей">
            <CloseIcon />
        </ActionBarButton>
    {/if}
</div>
{#if $userListStore}
    <div
        data-testid="room-user-list-popup"
        class="absolute"
        use:floatingUiContent
        use:clickOutside={() => userListStore.close()}
    >
        <RoomUserListWrapper />
    </div>
{/if}
